<template>
  <!-- [고객 정보 추가 팝업] : 로그인id/이름 + 후원인/추천인/소속센터. 저장 시 신규 등록 후 후원/추천/센터 반영 -->
  <div class="modal-mask" @click.self="$emit('close')">
    <div class="modal">
      <!-- 상단 : 좌측 = 후원인/추천인 설정해제, 우측 = 저장/취소(닫기) -->
      <div class="head">
        <button class="mini warn" :disabled="!canEdit" @click="unsetSponsor">후원인 설정해제</button>
        <button class="mini warn" :disabled="!canEdit" @click="unsetReferrer">추천인 설정해제</button>
        <span class="spacer"></span>
        <button class="primary" :disabled="!canEdit || saving" @click="save">저장</button>
        <button class="ghost" @click="$emit('close')">취소</button>
      </div>
      <div class="titlebar">고객 정보 추가</div>

      <div class="body">
        <!-- 로그인id -->
        <div class="row">
          <div class="cap">로그인ID</div>
          <div class="val">
            <input v-model="loginId" type="text" :disabled="!canEdit" placeholder="" @keyup.enter="save" />
          </div>
        </div>

        <!-- 이름 -->
        <div class="row">
          <div class="cap">이름</div>
          <div class="val">
            <input v-model="name" type="text" :disabled="!canEdit" placeholder="" @keyup.enter="save" />
          </div>
        </div>

        <!-- 후원인 : 이름 / 로그인id + 수정 -->
        <div class="row">
          <div class="cap">후원인</div>
          <div class="val">
            <span class="who">{{ label(sponsorName, sponsorLogin) }}</span>
            <button class="mini" :disabled="!canEdit" @click="openSponsor">수정</button>
          </div>
        </div>

        <!-- 추천인 : 이름 / 로그인id + 수정 -->
        <div class="row">
          <div class="cap">추천인</div>
          <div class="val">
            <span class="who">{{ label(referrerName, referrerLogin) }}</span>
            <button class="mini" :disabled="!canEdit" @click="openReferrer">수정</button>
          </div>
        </div>

        <!-- 소속 센터 : 센터명 + 수정. 진입 시 기본 = 본사 -->
        <div class="row">
          <div class="cap">소속 센터</div>
          <div class="val">
            <span class="who">{{ centerName || '-' }}</span>
            <button class="mini" :disabled="!canEdit" @click="centerOpen=true">수정</button>
          </div>
        </div>
      </div>

      <p v-if="err" class="err">{{ err }}</p>
    </div>

    <!-- 후원인/추천인 → 회원 검색 팝업 -->
    <SearchDialog v-if="searchOpen" @select="onSearchSelect" @close="closeSearch" />
    <!-- 소속 센터 → 센터 검색 팝업 -->
    <CenterSearchDialog v-if="centerOpen" @select="onCenterSelect" @close="centerOpen=false" />
  </div>
</template>

<script>
import api from '../api';
import { getAuth } from '../store/auth';
import SearchDialog from './SearchDialog.vue';
import CenterSearchDialog from './CenterSearchDialog.vue';

// 로그인ID 규칙: 영문 소문자+숫자 4~16자. 첫 글자는 소문자(숫자 시작/숫자로만 불가), 대문자·공백·특수문자 불가.
const LOGIN_ID_RE = /^[a-z][a-z0-9]{3,15}$/;
const HQ_CENTER_ID = 1;   // 본사

export default {
  name: 'CustomerAddDialog',
  components: { SearchDialog, CenterSearchDialog },
  data() {
    return {
      loginId: '', name: '',
      // 후원/추천 (저장 시 반영)
      sponsorName: '', sponsorLogin: '',
      referrerName: '', referrerLogin: '',
      // 소속 센터: 기본 = 본사
      centerId: HQ_CENTER_ID, centerName: '본사',
      searchOpen: false, searchMode: '', centerOpen: false,
      saving: false, err: '',
      canEdit: getAuth().role !== 'customer'   // customer 권한은 취소(닫기) 외 비활성화
    };
  },
  methods: {
    label(name, login) {
      if (!login && !name) return '-';
      return (name || '') + (login ? '(' + login + ')' : '');
    },
    // ----- 후원인/추천인 회원 검색 -----
    openSponsor() { if (!this.canEdit) return; this.searchMode = 'sponsor'; this.searchOpen = true; },
    openReferrer() { if (!this.canEdit) return; this.searchMode = 'referrer'; this.searchOpen = true; },
    onSearchSelect(sel) {
      this.searchOpen = false;
      if (this.searchMode === 'sponsor') {
        this.sponsorName = sel.name; this.sponsorLogin = sel.loginId;
      } else if (this.searchMode === 'referrer') {
        this.referrerName = sel.name; this.referrerLogin = sel.loginId;
      }
      this.searchMode = '';
    },
    closeSearch() { this.searchOpen = false; this.searchMode = ''; },
    // ----- 설정 해제 (아직 미등록 상태이므로 입력값만 비움) -----
    unsetSponsor() {
      if (!this.canEdit) return;
      if (!window.confirm('후원인 설정 해제하시겠습니까?')) return;
      this.sponsorName = ''; this.sponsorLogin = '';
    },
    unsetReferrer() {
      if (!this.canEdit) return;
      if (!window.confirm('추천인 설정 해제하시겠습니까?')) return;
      this.referrerName = ''; this.referrerLogin = '';
    },
    // ----- 소속 센터 -----
    onCenterSelect(c) {
      this.centerOpen = false;
      this.centerId = c.id;
      this.centerName = c.centerName || c.name || '';
    },
    // ----- 저장 : 신규 등록 후 후원/추천/센터 반영 -----
    async save() {
      if (this.saving || !this.canEdit) return;
      const lid = (this.loginId || '').trim();
      const nm = (this.name || '').trim();
      if (!lid) { this.err = '로그인ID를 입력하세요.'; return; }
      if (!nm) { this.err = '이름을 입력하세요.'; return; }
      if (!LOGIN_ID_RE.test(lid)) {
        this.err = '로그인ID는 영문 소문자와 숫자로 4~16자이며, 숫자로 시작할 수 없습니다. (대문자·공백·특수문자 불가)';
        return;
      }
      // 스펙234_개선_19: 소속 센터는 필수 입력 항목
      if (this.centerId == null || this.centerId === '') { this.err = '소속 센터를 설정하세요.'; return; }
      if (!window.confirm('저장하시겠습니까?')) return;
      this.saving = true; this.err = '';
      const actor = getAuth().loginId;
      try {
        const res = await api.createCustomer({ loginId: lid, name: nm });
        const newId = res && res.data ? res.data.id : null;
        if (newId != null) {
          // 스펙234_개선_30(재): 먼저 [주문사용자ID 조회 배치] 실행하여 주문사용자ID 설정.
          //  cafe24 토큰 오류 등으로 실패하면 다른 거래와 동일하게 다음 단계(후원/추천 설정)를 진행하지 않는다.
          await api.orderUserIdRescan(newId);
          // 이어서 추천인·후원인(·센터) 설정을 실행하며 저장 마무리
          if (this.sponsorLogin) {
            await api.sponsorSet({ customerId: newId, sponsorLoginId: this.sponsorLogin, actor });
          }
          if (this.referrerLogin) {
            await api.referrerSet({ customerId: newId, referrerLoginId: this.referrerLogin, actor });
          }
          // 기본(본사)에서 변경된 경우에만 센터 반영
          if (this.centerId != null && Number(this.centerId) !== HQ_CENTER_ID) {
            await api.centerSave({ customerId: newId, centerId: this.centerId, actor });
          }
        }
        this.$emit('saved');
      } catch (e) {
        this.err = (e && e.response && e.response.data && e.response.data.message) || e.message || '추가 실패';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-mask { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 60; }
.modal { background: #fff; padding: 20px 22px; border-radius: 8px; width: 560px; max-width: 96vw; max-height: 92vh; overflow: auto; }
.head { display: flex; align-items: center; gap: 8px; padding-bottom: 10px; }
.head .spacer { flex: 1; }
.head button { padding: 7px 16px; border-radius: 6px; cursor: pointer; border: 1px solid #ccc; }
.head .primary { background: #2f6fed; color: #fff; border-color: #2f6fed; }
.head .primary:disabled { background: #9db8ef; }
.head .ghost { background: #f5f5f5; }
.titlebar { border-bottom: 1px solid #eef; padding-bottom: 12px; margin-bottom: 14px; font-size: 15px; font-weight: 700; color: #1f2a44; }
.body { }
.row { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px dashed #eef; align-items: center; }
.cap { flex: 0 0 120px; color: #33415c; font-size: 13px; font-weight: 600; }
.val { flex: 1; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.val input { height: 32px; border: 1px solid #cfd6e6; border-radius: 6px; padding: 0 8px; flex: 0 0 240px; }
.val input:disabled { background: #f2f4f8; }
.who { font-size: 13px; }
.mini { padding: 3px 10px; font-size: 11px; background: #eef1f7; color: #2f6fed; border: 1px solid #cfdcff; border-radius: 4px; cursor: pointer; }
.mini.warn { background: #fff2f2; color: #c33; border-color: #f0c0c0; }
.mini:disabled { opacity: .5; cursor: not-allowed; }
.err { color: #c0392b; font-size: 13px; margin: 10px 0 0; }
</style>
